import { Check, Copy, ShieldCheck, ShieldOff, UserCheck, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Button as ShButton,
  Skeleton,
} from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../lib/intl.js';
import { platformApi } from '../../lib/platformApi.js';
import { usePlatformAuthStore } from '../../lib/platformAuthStore.js';
import { KpiCard } from '../reports/_shared/KpiCard.js';

interface AdminRow {
  id: string;
  email: string;
  fullName: string;
  status: 'active' | 'disabled';
  lastLoginAt: string | null;
  createdAt: string;
}

interface CreateResponse {
  admin: AdminRow;
  tempPassword: string;
}

export function PlatformAdminsPage(): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const me = usePlatformAuthStore((s) => s.admin);
  const [items, setItems] = useState<AdminRow[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: '', fullName: '' });
  const [newCreds, setNewCreds] = useState<CreateResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await platformApi().get<{ items: AdminRow[] }>('/admins');
      setItems(r.data.items);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => {
    if (!items) return null;
    return {
      total: items.length,
      active: items.filter((i) => i.status === 'active').length,
      disabled: items.filter((i) => i.status === 'disabled').length,
    };
  }, [items]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await platformApi().post<CreateResponse>('/admins', form);
      setNewCreds(r.data);
      setForm({ email: '', fullName: '' });
      setCreateOpen(false);
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code ?? String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id: string, status: 'active' | 'disabled') => {
    setError(null);
    setBusy(true);
    try {
      await platformApi().patch(`/admins/${id}`, { status });
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code ?? String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const copyPw = (p: string) => {
    void navigator.clipboard.writeText(p);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const columns: DataTableColumn<AdminRow>[] = [
    {
      id: 'email',
      header: t('admins.email'),
      sortValue: (row) => row.email,
      searchValue: (row) => row.email,
      cellClassName: 'font-medium',
      cell: (row) => (
        <>
          {row.email}
          {me?.id === row.id && (
            <Badge variant="secondary" className="ms-2 text-[0.65rem]">
              {t('admins.you')}
            </Badge>
          )}
        </>
      ),
    },
    {
      id: 'fullName',
      header: t('admins.fullName'),
      sortValue: (row) => row.fullName,
      searchValue: (row) => row.fullName,
      cell: (row) => row.fullName,
    },
    {
      id: 'status',
      header: t('admins.status'),
      sortValue: (row) => t(`admins.statusLabel.${row.status}`),
      searchValue: (row) => t(`admins.statusLabel.${row.status}`),
      cell: (row) => (
        <Badge variant={row.status === 'active' ? 'success' : 'secondary'}>
          {t(`admins.statusLabel.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: 'lastLogin',
      header: t('admins.lastLogin'),
      sortValue: (row) => row.lastLoginAt ?? '',
      cellClassName: 'text-xs text-fg-muted',
      cell: (row) =>
        row.lastLoginAt ? (
          formatDateTime(row.lastLoginAt, { locale: i18n.language })
        ) : (
          <span className="italic text-fg-subtle">
            {t('admins.neverLoggedIn', { defaultValue: 'Never' })}
          </span>
        ),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      headClassName: 'w-32',
      cell: (row) => {
        const isMe = me?.id === row.id;
        if (isMe) return null;
        const isDisableAction = row.status === 'active';
        return (
          <ShButton
            size="sm"
            variant="outline"
            className={
              isDisableAction
                ? 'border-warning/60 text-warning hover:bg-warning/10'
                : undefined
            }
            onClick={async () => {
              if (
                isDisableAction &&
                !(await confirmDialog({
                  message: t('admins.actions.confirmDisable', { email: row.email }),
                  tone: 'danger',
                }))
              ) {
                return;
              }
              void setStatus(row.id, isDisableAction ? 'disabled' : 'active');
            }}
            disabled={busy}
          >
            {t(isDisableAction ? 'admins.actions.disable' : 'admins.actions.enable')}
          </ShButton>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('admins.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('admins.subtitle')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus className="h-4 w-4" />
          {t('admins.newButton')}
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiCard
          tone="brand"
          icon={UserCheck}
          label={t('admins.kpi.total')}
          value={stats ? String(stats.total) : '—'}
          loading={!items}
        />
        <KpiCard
          tone="success"
          icon={ShieldCheck}
          label={t('admins.kpi.active')}
          value={stats ? String(stats.active) : '—'}
          loading={!items}
        />
        <KpiCard
          tone="warning"
          icon={ShieldOff}
          label={t('admins.kpi.disabled')}
          value={stats ? String(stats.disabled) : '—'}
          loading={!items}
        />
      </div>

      {error && (
        <p
          className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          role="alert"
        >
          {error}
        </p>
      )}

      {newCreds && (
        <Card className="border-warning/40 bg-warning/5 p-6">
          <h2 className="text-base font-semibold">{t('admins.created.title')}</h2>
          <p className="mt-1 text-sm text-fg-muted">{t('admins.created.hint')}</p>
          <dl className="mt-4 grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
            <dt className="text-fg-muted">{t('admins.email')}</dt>
            <dd className="font-medium">{newCreds.admin.email}</dd>
            <dt className="self-center text-fg-muted">{t('admins.created.tempPassword')}</dt>
            <dd className="flex items-center gap-2">
              <code className="select-all rounded-md border border-warning/40 bg-bg px-3 py-1.5 font-mono text-sm">
                {newCreds.tempPassword}
              </code>
              <ShButton size="sm" variant="outline" onClick={() => copyPw(newCreds.tempPassword)}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t('create.copied') : t('create.copy')}
              </ShButton>
            </dd>
          </dl>
          <div className="mt-4">
            <ShButton variant="ghost" size="sm" onClick={() => setNewCreds(null)}>
              {t('admins.created.dismiss')}
            </ShButton>
          </div>
        </Card>
      )}

      {items === null ? (
        <Card className="p-6"><Skeleton className="h-48 w-full" /></Card>
      ) : items.length === 0 ? (
        <EmptyState title={t('admins.empty')} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={items} rowKey={(row) => row.id} />
        </Card>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admins.create.title')}</DialogTitle>
          </DialogHeader>
          <form className="flex flex-col gap-3" onSubmit={onCreate} noValidate id="admin-create-form">
            <FormField id="adm-email" label={t('admins.email')} required>
              <Input
                id="adm-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
                autoFocus
              />
            </FormField>
            <FormField id="adm-full" label={t('admins.fullName')} required>
              <Input
                id="adm-full"
                value={form.fullName}
                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                required
              />
            </FormField>
          </form>
          <DialogFooter>
            <ShButton variant="outline" onClick={() => setCreateOpen(false)}>
              {t('detail.actions.cancel')}
            </ShButton>
            <Button type="submit" form="admin-create-form" loading={busy}>
              {t('admins.create.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
